<template>
  <div class="container px-2 pr-4">
    <div id="document-carousel-slide">
      <VueSlickCarousel v-bind="settings" v-if="isCarouselReady">
        <template #nextArrow>
          <button type="button" class="custom-arrow-next">
            <span class="png-icon png-icon-icons-glyph--ko-chevron-right"></span>
          </button>
        </template>
        <template #prevArrow>
          <button type="button" class="custom-arrow-prev">
            <span class="png-icon png-icon-icons-glyph--ko-chevron-left"></span>
          </button>
        </template>

        <!-- Lookbook PDF -->
        <div
          v-if="collectionData && collectionData.customData && collectionData.customData.lookBookPdf"
          class="text-center carousel-img-width"
        >
          <img
            class="widget-img-view"
            :src="imageData.IconLookBook"
            alt="img"
            @error="handleImageError"
          />
          <a
            class="font-size-12 text-uppercase caption-text-img lookbook-pdf-download"
            target="_blank"
            :href="collectionData.customData.lookBookPdf.replace(/^~\//, '/')"
          >
            {{ collectionData.customData.lookBookTitle }}
          </a>
        </div>

        <!-- Full Product Spec -->
        <div v-if="selectedProduct && selectedProduct.pdmsLink" class="text-center carousel-img-width">
          <img
            class="widget-img-view"
            :src="imageData.FullProductSpec"
            alt="img"
            @error="handleImageError"
          />
          <a
            class="font-size-12 text-uppercase caption-text spec-pdf-download"
            target="_blank"
            :href="selectedProduct.pdmsLink"
          >
            {{ $resourceString['product.Detail.Label.FullProductSpec'] }}
          </a>
        </div>

        <!-- Construction Spec -->
        <div v-if="!isResinous" class="text-center carousel-img-width">
          <img
            class="widget-img-view"
            :src="imageData.IconConstructionSpec"
            alt="img"
            @error="handleImageError"
          />
          <a
            class="font-size-12 text-uppercase caption-text technical-document-download"
            target="_blank"
            :href="'/Product/ConstructionSpec/' + (productData && productData.sellingStyleNumber)"
          >
            {{ $resourceString['product.Detail.Label.ConstructionSpec'] }}
          </a>
        </div>

        <!-- Installation Links -->
        <div v-if="productData && productData.installationLinks && productData.installationLinks.length" class="text-center carousel-img-width">
          <img
            class="widget-img-view"
            :src="imageData.IconInstallation"
            alt="img"
            @error="handleImageError"
          />
          <a
            class="font-size-12 text-uppercase caption-text"
            target="_blank"
            :href="productData.installationLinks[0].link"
          >
            {{ $resourceString['product.Detail.Title.Installation'] }}
          </a>
        </div>

        <!-- Maintenance Links -->
        <div v-if="productData && productData.maintenanceLinks && productData.maintenanceLinks.length" class="text-center carousel-img-width">
          <img
            class="widget-img-view"
            :src="imageData.IconMaintenance"
            alt="img"
            @error="handleImageError"
          />
          <a
            class="font-size-12 text-uppercase caption-text"
            target="_blank"
            :href="productData.maintenanceLinks[0].link"
          >
            {{ $resourceString['product.Detail.Title.Maintenance'] }}
          </a>
        </div>

        <!-- Warranty Links -->
        <div v-if="productData && productData.warrantyLinks && productData.warrantyLinks.length" class="text-center carousel-img-width">
          <img
            class="widget-img-view"
            :src="imageData.IconWarranty"
            alt="img"
            @error="handleImageError"
          />
          <a
            class="font-size-12 text-uppercase caption-text"
            target="_blank"
            :href="productData.warrantyLinks[0].link"
          >
            {{ $resourceString['product.Detail.Label.WarrantyLifetime'] }}
          </a>
        </div>
      </VueSlickCarousel>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import VueSlickCarousel from "vue-slick-carousel";
import "vue-slick-carousel/dist/vue-slick-carousel.css";
import "vue-slick-carousel/dist/vue-slick-carousel-theme.css";
import { globalIcons } from "../../../constants/global";

export default {
  props: {
    productData: {
      type: Object,
      default: () => ({}),
    },
    collectionData: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    VueSlickCarousel,
  },
  data() {
    return {
      settings: {
        centerMode: false,
        infinite: false,
        centerPadding: "15px",
        speed: 500,
        slidesToScroll: 1,
        variableWidth: true,
        responsive: [
          { breakpoint: 3840, settings: { slidesToShow: 4, slidesToScroll: 1 } },
          { breakpoint: 1440, settings: { slidesToShow: 3, slidesToScroll: 1 } },
          { breakpoint: 1024, settings: { slidesToShow: 2, slidesToScroll: 1 } },
          { breakpoint: 600, settings: { slidesToShow: 1, slidesToScroll: 1 } },
          { breakpoint: 480, settings: { slidesToShow: 1, slidesToScroll: 1 } },
        ],
      },
      imageData: {
        IconConstructionSpec: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(globalIcons.ConstructionSpec)}`,
        FullProductSpec: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(globalIcons.FullProductSpec)}`,
        IconWarranty: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(globalIcons.IconWarranty)}`,
        IconMaintenance: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(globalIcons.IconMaintenance)}`,
        IconInstallation: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(globalIcons.IconInstallation)}`,
        IconLookBook: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(globalIcons.IconLookBook)}`,
      },
      defaultImage: globalIcons.defaultImage,
    };
  },
  computed: {
    ...mapGetters({
      selectedProduct: "productDetail/selectedProduct",
    }),
    isResinous() {
      return (
        this.productData &&
        this.productData.inventoryType === this.$resourceString["resinous.InventoryType"]
      );
    },
    isCarouselReady() {
      return (
        (!this.isResinous && this.collectionData &&
          this.collectionData.customData &&
          this.collectionData.customData.lookBookPdf) ||
        (this.selectedProduct && this.selectedProduct.pdmsLink) ||
        (this.productData &&
          ((this.productData.installationLinks &&
            this.productData.installationLinks.length) ||
            (this.productData.maintenanceLinks &&
              this.productData.maintenanceLinks.length) ||
            (this.productData.warrantyLinks &&
              this.productData.warrantyLinks.length)))
      );
    },
  },
  methods: {
    handleImageError(event) {
      event.target.src = this.defaultImage;
    },
  },
};
</script>
